import * as Sentry from '@sentry/react';

import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

// Custom hook designed to log errors to Sentry, included on Error fallback component
// Handles both thrown error responses and plain exceptions from loaders/actions/lazy route loading
// For manual logging (ie. handled/soft error) - see `utils/sentryCaptureException`
const useSentryCaptureException = () => {
  const routeError: any = useRouteError();

  useEffect(() => {
    if (!routeError) return;

    // Custom error responses thrown from loaders/actions
    // "Soft" errors that are not explicitly thrown by loaders/actions are logged elsewhere via Sentry.captureException
    if (isRouteErrorResponse(routeError)) {
      // Skip logging 404 errors
      if (routeError.status === 404) return;

      let errorMessage = 'Unknown Error';
      let extraData = {};
      if (routeError.data) {
        errorMessage = `${routeError.data.errorLoc}: Error on Route: ${routeError.data.route} - Step: ${routeError.data.step}`;
        extraData = routeError.data.extra ?? {};
      }
      Sentry.captureException(new Error(errorMessage), { level: 'fatal', extra: extraData });
      return;
    }

    // Plain exceptions (ie. a TypeError in a loader) are caught by React Router to render this fallback,
    // so they never reach Sentry's global handlers - log them here
    Sentry.captureException(routeError, { level: 'fatal' });
  }, [routeError]);

  return null;
};

export default useSentryCaptureException;
